/**
 * AWS Amplify Configuration
 *
 * Configures AWS Cognito authentication with OAuth providers.
 * Environment variables:
 * - VITE_COGNITO_USER_POOL_ID: Cognito User Pool ID
 * - VITE_COGNITO_CLIENT_ID: Cognito App Client ID
 * - VITE_COGNITO_OAUTH_DOMAIN: OAuth domain (e.g., auth.anyw3ar.com)
 * - VITE_COGNITO_REDIRECT_SIGN_IN: OAuth redirect after sign-in
 * - VITE_COGNITO_REDIRECT_SIGN_OUT: OAuth redirect after sign-out
 */

import { ResourcesConfig } from 'aws-amplify';

/**
 * Validate required environment variables
 */
const requiredEnvVars = {
  USER_POOL_ID: import.meta.env.VITE_COGNITO_USER_POOL_ID,
  USER_POOL_CLIENT_ID: import.meta.env.VITE_COGNITO_CLIENT_ID,
  OAUTH_DOMAIN: import.meta.env.VITE_COGNITO_OAUTH_DOMAIN,
  REDIRECT_SIGN_IN: import.meta.env.VITE_COGNITO_REDIRECT_SIGN_IN,
  REDIRECT_SIGN_OUT: import.meta.env.VITE_COGNITO_REDIRECT_SIGN_OUT,
};

const missingVars = Object.entries(requiredEnvVars)
  .filter(([, value]) => !value)
  .map(([key]) => `VITE_${key}`);

if (missingVars.length > 0) {
  throw new Error(
    `Missing required environment variables: ${missingVars.join(', ')}\n` +
    'Please ensure all VITE_* variables are set in your .env file.'
  );
}

/**
 * Amplify Auth Configuration
 *
 * Supports:
 * - Email/password authentication
 * - Google OAuth (Social Sign-In)
 * - Apple OAuth (Social Sign-In)
 */
export const amplifyConfig: ResourcesConfig = {
  Auth: {
    Cognito: {
      userPoolId: requiredEnvVars.USER_POOL_ID!,
      userPoolClientId: requiredEnvVars.USER_POOL_CLIENT_ID!,
      loginWith: {
        oauth: {
          domain: requiredEnvVars.OAUTH_DOMAIN!,
          scopes: ['openid', 'email', 'profile'],
          redirectSignIn: [requiredEnvVars.REDIRECT_SIGN_IN!],
          redirectSignOut: [requiredEnvVars.REDIRECT_SIGN_OUT!],
          responseType: 'code', // Authorization code flow (more secure)
          providers: ['Google', 'Apple'], // Note: NOT 'Facebook' as per requirements
        },
      },
    },
  },
};

// Debug logging (remove after OAuth is working)
console.log('[Amplify Config] OAuth Configuration:', {
  domain: requiredEnvVars.OAUTH_DOMAIN,
  redirectSignIn: requiredEnvVars.REDIRECT_SIGN_IN,
  redirectSignOut: requiredEnvVars.REDIRECT_SIGN_OUT,
  userPoolId: requiredEnvVars.USER_POOL_ID,
  clientId: requiredEnvVars.USER_POOL_CLIENT_ID,
  providers: amplifyConfig.Auth?.Cognito?.loginWith?.oauth?.providers,
});
